import { AritoFormV2, HeaderTabsV2 } from '@/components/custom/erp';
import BottomBar from './BottomBar';

import { useEffect, useMemo, useState } from 'react';
import { getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { calculateTotals, handleUpdateRowFields } from '../../utils/calc-util';

import { formSchema, initialFormValues } from '../../schema';

import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useDonHangSearchContext } from '../../context';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { MA_CHUNG_TU } from '@/constants';
import { FormMode } from '@/types/form';

import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';
import { OtherTab } from './OtherTab';

interface FormDialogProps {
  formMode: FormMode;
  open: boolean;
  initialData?: BanHangRecord;
  onSubmit?: (data: BanHangPayload) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entity, entityUnit } = useAuth();
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs,
    isPasting: detailIsPasting
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields);
  const { state, actions } = useFormFieldState(initialData);
  const { transactionOptions } = useDonHangSearchContext();

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  const watchCols = detailRows
    .map((r: any) => `${r.ma_vt_data?.uuid || r.ma_vt}-${r.dvt_data?.uuid || r.dvt}-${r.ma_kho_data?.uuid || r.ma_kho}`)
    .join(',');
  const currentNgayCt = initialData?.ngay_ct || initialFormValues.ngay_ct;

  useEffect(() => {
    let isMounted = true;
    const fetchStock = async () => {
      if (!detailRows.length || !entity?.slug || !currentNgayCt) {
        return;
      }

      try {
        const items = detailRows
          .map((row: any) => ({
            ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
            dvt: row.dvt_data?.uuid || row.dvt || row.ma_vt_data?.dvt_nl || row.ma_vt_data?.dvt || '',
            ma_kho: row.ma_kho_data?.uuid || row.ma_kho || ''
          }))
          .filter((item: any) => item.ma_vt);

        if (items.length === 0) return;

        const { fetchSoDuTonKho } = await import('../../utils');
        const response = await fetchSoDuTonKho(entity.slug, String(currentNgayCt), items);

        if (isMounted) {
          let hasChanges = false;
          const newRows = detailRows.map((row: any) => {
            const vtUuid = row.ma_vt_data?.uuid || row.ma_vt;
            const khoUuid = row.ma_kho_data?.uuid || row.ma_kho;
            const found = response.results.find(r => r.ma_vt === vtUuid && (r.ma_kho === khoUuid || !r.ma_kho));
            if (found && Number(found.so_du) !== row.sl_ton) {
              hasChanges = true;
              return { ...row, sl_ton: Number(found.so_du) };
            }
            return row;
          });
          if (hasChanges) {
            setDetailRows(newRows);
          }
        }
      } catch (error) {
        console.error('Failed to fetch stock for detail tab', error);
      }
    };

    const timeout = setTimeout(() => {
      fetchStock();
    }, 500);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchCols, currentNgayCt, entityUnit?.slug, setDetailRows]);

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const totals = useMemo(() => calculateTotals(detailRows), [detailRows]);

  const normalizeSubmitData = (data: BanHangPayload) => {
    const defaultMaGD =
      formMode === 'add'
        ? transactionOptions[0]?.value || initialFormValues.ma_gd || 'NB'
        : initialData?.ma_gd ||
          initialData?.ma_gd_data?.uuid ||
          transactionOptions[0]?.value ||
          initialFormValues.ma_gd ||
          'NB';

    return {
      ...initialFormValues,
      ...data,
      ma_gd: data.ma_gd || defaultMaGD
    };
  };

  const handleSubmit = (data: BanHangPayload) => {
    const submitData = normalizeSubmitData(data);
    const formData = transformFormData(
      submitData,
      state,
      totals,
      detailRows,
      entityUnit,
      formMode,
      initialData?.chi_tiet_data
    );

    if (formMode === 'edit') {
      onSubmit?.({ ...formData });
      return;
    }

    onSubmit?.(formData);
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    onClose
  });

  return (
    <AritoFormV2
      mode={formMode}
      initialData={initialData || initialFormValues}
      title={title}
      schema={formSchema}
      actionButtons={actionButtons}
      subTitle='Đơn hàng'
      onSubmit={handleSubmit}
      onClose={onClose}
      headerFields={
        <div>
          <HeaderTabsV2
            tabs={[
              {
                id: 'info',
                label: 'Thông tin',
                component: <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              }
            ]}
            actionButtons={
              formMode !== 'add' && initialData?.uuid ? (
                <InfoDropdown
                  uuid={initialData.uuid}
                  ma_ct={initialData?.chung_tu || initialData?.ma_ct || MA_CHUNG_TU.BAN_HANG.DON_HANG}
                />
              ) : null
            }
            onTabChange={handleTabChange}
            defaultTabIndex={activeTab === 'info' ? 0 : activeTab === 'history' ? 1 : 0}
          />
        </div>
      }
      tabs={
        activeTab === 'info' && [
          {
            id: 'details',
            label: 'Chi tiết',
            component: (
              <div className='flex h-full flex-col overflow-hidden'>
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  onRepeatForRows={handleRepeatDetailRows}
                  handleUpdateNonWritableFields={detailSetNonWritableFields}
                  handleUpdatePasteConfigs={detailSetPasteConfigs}
                  constraints={state.khachHang?.account_data?.constraints ?? []}
                  isPasting={detailIsPasting}
                />
              </div>
            )
          },
          {
            id: 'other',
            label: 'Khác',
            component: (
              <div className='h-full overflow-auto'>
                <OtherTab formMode={formMode} formState={{ state, actions }} />
              </div>
            )
          }
        ]
      }
      bottomBar={activeTab === 'info' && <BottomBar totals={totals} state={state} />}
    />
  );
};

export default FormDialog;
